import {ariaLabelDate} from '@github-ui/aria-label-date'
import type {LineRange} from '@github-ui/diffs/types'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useAnalytics} from '@github-ui/use-analytics'
import {useReviewThreadCollapseState} from '@github-ui/use-review-thread-collapse-state'
import {StopIcon} from '@primer/octicons-react'
import {Button, Flash, Spinner} from '@primer/react'
import {Suspense, useMemo, useRef, useState} from 'react'

import {CommentErrorFallback} from './CommentErrorFallback'
import {useInlineCommentDialogModeInteractiveElements} from '../../hooks/use-inline-comment-dialog-mode-interactive-elements'
import type {
  CommentAuthor,
  ConfigureSuggestedChangesImplementation,
  ResolutionReasonType,
} from '../../conversation-types'
import {AutomatedCommentSource, CommentReviewVariantType, isCopilotCodeReviewThread} from '../../conversation-types'
import styles from './InlineReviewThread.module.css'
import type {ReviewThreadProps} from './ReviewThread'
import {ReviewThread} from './ReviewThread'
import {ReviewThreadHeader} from './ReviewThreadHeader'
import {useDialogModeActions, useDialogModeIsEnabled} from '../../stores/selectors'
import {useComparisonInfo, useSelectedCommentId, useThread} from '@github-ui/pull-requests/pull-request-store-selectors'
import {useResolveThreadMutation} from '@github-ui/pull-requests/use-resolve-thread-mutation'
import {useUnresolveThreadMutation} from '@github-ui/pull-requests/use-unresolve-thread-mutation'
import type {LineKey} from '../../types'
import {InlineAutomatedReviewThreadActions} from './InlineAutomatedReviewThreadActions'
import {ResolveThreadSplitButton} from './ResolveThreadSplitButton'
import {isLoggedIn} from '@github-ui/client-env'

export interface InlineReviewThreadProps extends Pick<ReviewThreadProps, 'batchingEnabled' | 'viewerData'> {
  threadId: string
  fileAnchor?: string
  filePath: string
  lineMarkersKey: LineKey
  gridCellId: string
  onDeleteLineComment: () => void
  threadsConnectionId?: string
  ghostUser?: CommentAuthor
  isFirstThread?: boolean
  threadPositionNumber?: number
  suggestedChangesConfig?: ConfigureSuggestedChangesImplementation
}

/**
 * The InlineReviewThread opens from the ActionBar when the user selects a conversation.
 */
export function InlineReviewThread({
  fileAnchor,
  filePath,
  gridCellId,
  onDeleteLineComment,
  threadId,
  threadPositionNumber,
  lineMarkersKey,
  threadsConnectionId,
  isFirstThread,
  ghostUser,
  suggestedChangesConfig,
  ...rest
}: InlineReviewThreadProps) {
  const thread = useThread(threadId, filePath, lineMarkersKey)
  const threadResolutionReasonEnabled = isFeatureEnabled('thread_resolution_reason')
  const showResolutionReasons = threadResolutionReasonEnabled && thread != null && isCopilotCodeReviewThread(thread)
  const {selectedRange} = useComparisonInfo()

  const {mutate: resolveThreadMutation} = useResolveThreadMutation(filePath, lineMarkersKey)
  const handleResolveThread = (resolutionReason?: ResolutionReasonType) => {
    if (!thread) return

    resolveThreadMutation(
      {threadId: thread.id, resolutionReason},
      {
        onError: () => setErrorMessage('Failed to resolve thread'),
        onSuccess: () => {
          setIsCollapsed(true)
        },
      },
    )

    sendAnalyticsEvent('comments.resolve_thread', 'RESOLVE_CONVERSATION_BUTTON')
  }

  const reviewThreadRef = useRef<HTMLDivElement>(null)
  const isResolved = thread?.isResolved
  const [errorMessage, setErrorMessage] = useState('')
  const [isCollapsed, setIsCollapsed] = useReviewThreadCollapseState(threadId, isResolved ?? false)
  const isInDialogMode = useDialogModeIsEnabled(gridCellId)
  const {disableDialogMode, enableDialogMode} = useDialogModeActions()

  const selectedCommentId = useSelectedCommentId()

  // Expand thread temporarily (without persisting) when navigating from the side panel
  const shouldForceExpand = useMemo(() => {
    if (selectedCommentId == null) return false
    return thread?.commentsData?.comments.some(comment => comment.databaseId === selectedCommentId) ?? false
  }, [selectedCommentId, thread])

  const effectiveIsCollapsed = isCollapsed && !shouldForceExpand

  const handleToggleCollapsed = () => {
    setIsCollapsed(!effectiveIsCollapsed)
  }

  useInlineCommentDialogModeInteractiveElements({
    commentSubjectType: 'pull request',
    gridCellId,
    markerRef: reviewThreadRef,
    isThread: true,
    thread,
  })

  const config = useMemo(() => {
    const positioning = thread?.positioning ?? thread?.subject // support both for backwards compatibility in the interim

    if (!positioning) {
      return undefined
    }

    if (
      fileAnchor &&
      thread &&
      positioning.startDiffSide &&
      positioning.endDiffSide &&
      positioning.startLine &&
      positioning.endLine &&
      suggestedChangesConfig?.configureSuggestedChangesFromLineRange
    ) {
      const startLine = positioning.startLine ?? positioning.endLine
      const startDiffSide = positioning.startDiffSide ?? positioning.endDiffSide

      const threadRowRange: LineRange = {
        diffAnchor: fileAnchor,
        endLineNumber: positioning.endLine,
        endOrientation: positioning.endDiffSide === 'LEFT' ? 'left' : 'right',
        startLineNumber: startLine,
        startOrientation: startDiffSide === 'LEFT' ? 'left' : 'right',
        firstSelectedLineNumber: startLine,
        firstSelectedOrientation: startDiffSide === 'LEFT' ? 'left' : 'right',
      }
      return suggestedChangesConfig?.configureSuggestedChangesFromLineRange(threadRowRange)
    }

    return undefined
  }, [fileAnchor, thread, suggestedChangesConfig])

  const {sendAnalyticsEvent} = useAnalytics()

  const {mutate: unresolveThreadMutation} = useUnresolveThreadMutation(filePath, lineMarkersKey)
  const handleUnresolveThread = () => {
    if (!thread) return

    unresolveThreadMutation(
      {threadId: thread.id},
      {
        onError: () => setErrorMessage('Failed to unresolve thread'),
        onSuccess: () => {
          setIsCollapsed(false)
        },
      },
    )

    sendAnalyticsEvent('comments.unresolve_thread', 'RESOLVE_CONVERSATION_BUTTON')
  }

  const isAutomatedReviewThread =
    thread?.commentsData?.comments[0]?.reviewVariantType === CommentReviewVariantType.Automated
  const automatedComment = isAutomatedReviewThread ? thread.commentsData?.comments[0]?.automatedComment : null
  const isDismissableAutomatedComment = Boolean(automatedComment && automatedComment.dismissable)
  const licenseComplianceAutomatedComment =
    automatedComment && automatedComment.source === AutomatedCommentSource.LicenseCompliance

  if (!thread) return null

  const hasComments = (thread.commentsData?.comments?.length ?? 0) > 0

  if (!hasComments) return null

  const isThreadResolved = !!thread.isResolved
  const userCanResolve = isLoggedIn()

  const isResolveable =
    userCanResolve && thread.commentsData?.comments?.some(comment => comment.state?.toUpperCase() !== 'PENDING')

  const createdAt = thread.commentsData?.comments?.[0]?.createdAt
  const commentAuthorLogin = thread.commentsData?.comments?.[0]?.author?.login ?? 'Unknown Author'

  let resolveButtonLabel = `${isResolved ? 'Unresolve' : 'Resolve'} comment`
  if (createdAt) {
    const createdAtFormatted = ariaLabelDate(new Date(createdAt))
    resolveButtonLabel = `${
      isResolved ? 'Unresolve' : 'Resolve'
    } ${commentAuthorLogin}'s comment, ${createdAtFormatted}`
  }

  return (
    <ErrorBoundary boundaryName="InlineReviewThread" fallback={<CommentErrorFallback />}>
      <div className="rounded-2 bgColor-default" ref={reviewThreadRef}>
        <ReviewThreadHeader
          thread={thread}
          isCollapsed={effectiveIsCollapsed}
          isFirstThread={isFirstThread}
          onToggleCollapsed={handleToggleCollapsed}
        />
        <Suspense
          fallback={
            <div className={styles.ThreadLoading}>
              <Spinner />
              <p>Loading comments</p>
            </div>
          }
        >
          {errorMessage && (
            <Flash variant="danger" className="m-2">
              <StopIcon className="mr-2" />
              {errorMessage}
            </Flash>
          )}
          {!effectiveIsCollapsed && (
            <>
              <ReviewThread
                lineMarkersKey={lineMarkersKey}
                disableDialogMode={disableDialogMode}
                enableDialogMode={enableDialogMode}
                filePath={filePath}
                gridCellId={gridCellId}
                onDeleteLineComment={onDeleteLineComment}
                isInlineComment
                isInDialogMode={isInDialogMode}
                thread={thread}
                threadPositionNumber={threadPositionNumber}
                threadsConnectionId={threadsConnectionId}
                suggestedChangesConfig={config}
                shouldLimitHeight={false}
                selectedRange={selectedRange}
                {...rest}
                ghostUser={ghostUser}
              />
              {isResolveable && !isDismissableAutomatedComment && !licenseComplianceAutomatedComment && (
                <div className="d-flex flex-items-center p-2 border-top flex-wrap">
                  {isThreadResolved ? (
                    <Button aria-label={resolveButtonLabel} onClick={handleUnresolveThread} className="mr-2">
                      Unresolve comment
                    </Button>
                  ) : showResolutionReasons ? (
                    <div className="mr-2">
                      <ResolveThreadSplitButton
                        resolveButtonLabel={resolveButtonLabel}
                        onResolve={handleResolveThread}
                      />
                    </div>
                  ) : (
                    <Button aria-label={resolveButtonLabel} onClick={() => handleResolveThread()} className="mr-2">
                      Resolve comment
                    </Button>
                  )}
                  {isThreadResolved && thread.resolvedBy && (
                    <span className="fgColor-muted f6">
                      <b>{thread.resolvedBy}</b> marked this comment as resolved
                    </span>
                  )}
                </div>
              )}
              {isDismissableAutomatedComment && automatedComment && (
                <InlineAutomatedReviewThreadActions
                  automatedComment={automatedComment}
                  thread={thread}
                  filePath={filePath}
                  lineMarkersKey={lineMarkersKey}
                  setErrorMessage={setErrorMessage}
                  setIsCollapsed={setIsCollapsed}
                />
              )}
            </>
          )}
        </Suspense>
      </div>
    </ErrorBoundary>
  )
}
